import { Auth0ContextInterface } from '@auth0/auth0-react';
import axios, { AxiosError } from 'axios';
import config from './config/config';
import { withSilentAccessToken } from './infrastructure/helper';
import { THEME_BOX_STYLES } from '@lemontaps/lib/constants';
import { ThemeBoxStyle } from '@lemontaps/lib/types/global';
import { DEFAULT_AUTH_SCOPES } from './infrastructure/hoc/LtAuth0Provider';

export const themeCss = (boxStyle: ThemeBoxStyle): string => {
  switch (boxStyle) {
    case THEME_BOX_STYLES.ANGULAR:
      return 'theme-angular';
    case THEME_BOX_STYLES.ROUNDED:
      return 'theme-rounded';
    default:
      return 'theme-rounded';
  }
};

export const removePrefix = (val: string, pref: string): string => {
  if (val.startsWith(pref)) {
    return val.substring(pref.length);
  } else {
    return val;
  }
};

export const getFileName = (type: string | undefined, file: File | undefined) => {
  if (!file) return '';
  const ext = file.name.split('.').pop();
  if (type === 'profile') {
    return 'profile-picture.' + ext;
  } else if (type === 'banner') {
    return 'banner.' + ext;
  } else if (type === 'logoHeader') {
    return 'logo.' + ext;
  }

  return file.name;
};

// src: https://betterprogramming.pub/loading-third-party-scripts-dynamically-in-reactjs-458c41a7013d
export const loadJsScript = (
  url: string,
  scriptId: string,
  config?: {
    cb?: () => void;
    attributes?: Array<[string, string]>;
    async?: boolean;
  },
) => {
  const { cb, attributes } = config || {};
  const script = document.getElementById(scriptId);
  if (!script) {
    const script = document.createElement('script');
    script.src = url;
    script.id = scriptId;
    if (config?.async) script.async = true;
    if (attributes) {
      for (const attrs of attributes) {
        script.setAttribute(attrs[0], attrs[1]);
      }
    }
    document.body.appendChild(script);
    script.onload = () => {
      if (cb) cb();
    };
  }
  if (script && cb) cb();
};

// check if time diff is smaller then maxDiff
export const isTimeDiffSmaller = (
  val0: string | number,
  val1: number,
  maxDiff: number,
): boolean => {
  const parsed = typeof val0 === 'string' ? parseInt(val0) : val0;
  if (typeof parsed === 'number') {
    return val1 - parsed < maxDiff;
  } else {
    return true;
  }
};

type Event =
  | 'email-verified'
  | 'onboarding-completed'
  | 'qr-bg-download'
  | 'qr-page-skipped'
  | 'meta-page-skipped'
  | 'qr-page-completed'
  | 'add-to-wallet-clicked'
  | 'add-to-wallet-skipped'
  | 'add-to-wallet-completed'
  | 'how-to-tap-completed';

export const onboardingNotify = (
  event: Event,
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
) =>
  withSilentAccessToken(getAccessTokenSilently, token =>
    axios.post(
      config.API_BASE_URL + 'onboarding/notify',
      {
        key: event,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    ),
  ).catch(() => {});

export const showCookieSidebar = () => {
  window.UC_UI?.showSecondLayer?.();
};

export const isCustomDomain = () => {
  return window.location.origin !== config.REACT_APP_URL;
};

export const getAuth0TokenScopeString = (...additionalScopes: string[]) =>
  isCustomDomain() ? DEFAULT_AUTH_SCOPES : [...additionalScopes, DEFAULT_AUTH_SCOPES].join(' ');

export const lexicalSortObject =
  (attributeName: string) => (a: Record<string, string>, b: Record<string, string>) => {
    const textA = a[attributeName].toUpperCase();
    const textB = b[attributeName].toUpperCase();
    return textA < textB ? -1 : textA > textB ? 1 : 0;
  };

export const openLink = (url: string, target = '_self') => {
  const link = document.createElement('a');
  link.style.display = 'none';
  link.rel = 'noopener noreferrer';
  link.href = url;
  link.target = target;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => {
      console.log(error);
      resolve('');
    };
  });
};

export const flexCenterProps = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
} as const;

export const getValueByPath = <T extends object>(obj: T, path: string) => {
  // @ts-expect-error path is an object path
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

export function toKebabCase(text: string): string {
  return text
    .replace(/([a-z])([A-Z])/g, '$1-$2') // Insert a dash between lowercase and uppercase letters
    .replace(/[\s_]+/g, '-') // Replace spaces and underscores with a dash
    .toLowerCase(); // Convert the whole string to lowercase
}

export const isAxiosError = (error: unknown): error is AxiosError => {
  return typeof error === 'object' && error !== null && 'response' in error && 'message' in error;
};
